import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  MailOutline as MailIcon,
  LockOutlined as LockIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import ResendConfirmation from './ResendConfirmation';
import { safeNext } from '../../utils/safeNext';

/**
 * Signing back in.
 *
 * The dark header this used to wear was the boilerplate's, and it made the
 * form look like a security checkpoint rather than the door of a
 * neighbourhood hub. What is left is the two fields, the error, and one way
 * out towards sign-up.
 */
function LoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [unconfirmed, setUnconfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Where they were going before they were asked to sign in. Only a path of
  // our own: see safeNext for why that takes more than a prefix check.
  const destination = safeNext(searchParams.get('next')) ?? '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setUnconfirmed(false);
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      setEmail('');
      setPassword('');
      navigate(destination);
    } else {
      setError(result.error);
      setUnconfirmed('email_not_confirmed' === result.code);
    }

    setLoading(false);
  };

  return (
    <Card sx={{ maxWidth: 520, width: '100%', borderRadius: '22px' }}>
      <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
          <Typography variant="h2" component="h1">
            Bon retour&nbsp;!
          </Typography>
          <Typography variant="subtitle1" component="p">
            Connectez-vous pour écrire sur le fil, prendre rendez-vous et suivre l'entretien de
            votre logement.
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            label="Adresse électronique"
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            disabled={loading}
            autoComplete="email"
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Mot de passe"
            type={showPassword ? 'text' : 'password'}
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            disabled={loading}
            autoComplete="current-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                    edge="end"
                    // Named, not just pictured: the eye alone does not say
                    // which way it is about to go.
                    title={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Typography variant="body2" sx={{ mt: -1.5, textAlign: 'right' }}>
            <Link component={RouterLink} to="/mot-de-passe-oublie">
              Mot de passe oublié&nbsp;?
            </Link>
          </Typography>

          {error && !unconfirmed && <Alert severity="error">{error}</Alert>}

          {/* Not an error so much as a step left: the password was right. */}
          {unconfirmed && (
            <Alert severity="warning">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'flex-start' }}>
                {error}
                <ResendConfirmation email={email} />
              </Box>
            </Alert>
          )}

          <Button type="submit" variant="contained" size="large" fullWidth disabled={loading} sx={{ mt: 0.5 }}>
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1.5, color: 'inherit' }} />
                Connexion…
              </>
            ) : (
              'Se connecter'
            )}
          </Button>

          <Typography variant="body2" align="center" sx={{ mt: 0.5 }}>
            Pas encore de compte&nbsp;?{' '}
            <Link component={RouterLink} to="/register">
              Créer un compte
            </Link>
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export default LoginForm;
